'use client'

import { compactClassName } from '@/components/homepage-october/compact'
import { DestinationLink } from '../blocks/DestinationLink'
import { RetailerMark } from '@/components/homepage-2026/retailer-mark'

import * as React from 'react'
import {
  Award,
  BookOpen,
  Building2,
  ChevronRight,
  Columns2,
  Cpu,
  CupSoda,
  Cuboid,
  Beer,
  PanelTop,
  PanelBottom,
  Droplet,
  GraduationCap,
  House,
  Leaf,
  Maximize2,
  Minimize2,
  Palette,
  Refrigerator,
  Ruler,
  Snowflake,
  VolumeX,
  Warehouse,
  Wine,
  Zap,
  type LucideIcon,
} from '@/components/homepage-october/lucide'

import { Badge } from '@/components/homepage-october/ui/badge'
import { Button } from '@/components/homepage-october/ui/button'
import { Card } from '@/components/homepage-october/ui/card'
import { Carousel, CarouselContent, CarouselItem, type CarouselApi } from '@/components/homepage-october/ui/carousel'
import { Item, ItemActions, ItemContent, ItemMedia, ItemTitle } from '@/components/homepage-october/ui/item'
import { cn } from '@/lib/utils'
import { ArrowRight } from '../blocks/icons'
import { Img } from '../blocks/Img'
import { ChipLinks, ChipRows, Eyebrow, Section, SectionHeader, ViewAll, words } from '../blocks/layout'
import { RailNav } from '../blocks/Rail'
import { useDragScroll, useReducedMotion } from '../hooks'
import type { Model, Tile } from '../types'

/* ------------------------------------------------------------------ shared bits */

/** Soft photo-well tints, cycled so a grid of product photos reads as colour, never as grey. */
const WELLS = [
  'from-[#e4efff] to-[#f7faff]',
  'from-[#ffeadb] to-[#fff8f2]',
  'from-[#ddf4e6] to-[#f5fbf7]',
  'from-[#ebe4ff] to-[#f9f7ff]',
  'from-[#fcedd3] to-[#fffaf1]',
  'from-[#d9f1f8] to-[#f3fbfd]',
  'from-[#ffe2e9] to-[#fff6f8]',
  'from-[#e7efd4] to-[#f9fbf2]',
]
const well = (i: number) => WELLS[i % WELLS.length]

/** Brand ink keeps category navigation quiet beside the product photography. */
const CHIP_TINTS = ['bg-[#eaf2ff]! text-[#0454c0]']

/** Keyword → icon, after v2's own picks (award, snow, home, compare, ruler, leaf, cpu, wine, drop…). */
const ICONS: [RegExp, LucideIcon][] = [
  [/energy guide/i, Zap],
  [/guide/i, BookOpen],
  [/^best .*refrig/i, Award],
  [/^best .*freez/i, Snowflake],
  [/french door/i, Refrigerator],
  [/side.?by.?side/i, Columns2],
  [/dorm/i, GraduationCap],
  [/mini|undercounter/i, Minimize2],
  [/inch wide|counter.?depth/i, Ruler],
  [/energy/i, Leaf],
  [/smart/i, Cpu],
  [/large/i, Maximize2],
  [/quiet/i, VolumeX],
  [/wine/i, Wine],
  [/beverage|beer fridge/i, CupSoda],
  [/kegerator|draft beer/i, Beer],
  [/top.?freezer/i, PanelTop],
  [/bottom.?freezer/i, PanelBottom],
  [/garage/i, Warehouse],
  [/commercial/i, Building2],
  [/water/i, Droplet],
  [/\bice\b/i, Cuboid],
  [/freez/i, Snowflake],
  [/apartment|small kitchen/i, House],
  [/stainless|white|black/i, Palette],
]
export const iconFor = (text: string): LucideIcon => ICONS.find(([re]) => re.test(text))?.[1] ?? Refrigerator

/** Keyframes this file needs (app.css is shared). Every element that runs them carries data-motion, so the global
 *  reduced-motion rule stops it. */

/** The `--sw` paint from a v2 band style string ("--sw:#16181b;--fg:#ffffff"). */
const swOf = (style: string) => /--sw:([^;]+)/.exec(style)?.[1]?.trim()

/* ------------------------------------------------------------------ V05: shop by type */

function TypeTile({ t, i, wide }: { t: Tile; i: number; wide?: boolean }) {
  return (
    <div className={compactClassName("group relative h-full")}>
      <DestinationLink
        href={t.href}
        aria-label={t.aria}
        className="flex h-full flex-col overflow-hidden rounded-2xl bg-white shadow-[0_1px_2px_rgba(16,24,40,0.06)] ring-1 ring-black/[0.06] transition duration-300 hover:-translate-y-1 hover:shadow-[0_22px_40px_-24px_rgba(4,105,240,0.55)] hover:ring-[#0469f0]/25"
      >
        <span className={compactClassName(cn('relative block h-28 overflow-hidden bg-gradient-to-b sm:h-44 lg:h-48 xl:h-36', well(i)))}>
          <span aria-hidden="true" className={compactClassName("absolute inset-x-[24%] bottom-3 h-3 rounded-[50%] bg-[#1a1a1a]/10 blur-md")} />
          <Img
            src={t.img.src}
            alt={t.img.alt}
            className={cn('relative size-full mix-blend-multiply transition duration-500 group-hover:scale-[1.05]', t.href === '/commercial-refrigerators' ? 'p-2' : 'p-4', wide && 'sm:px-12 lg:px-4')}
          />
        </span>
        <span className={compactClassName("flex flex-1 items-center justify-between gap-2 px-3.5 py-3")}>
          <span className={compactClassName("text-sm font-semibold leading-snug text-neutral-900 transition-colors group-hover:text-[#0454c0] md:text-[15px] xl:text-[13px]")}>{t.label}</span>
          <ArrowRight aria-hidden="true" className="size-4 shrink-0 text-neutral-400 transition group-hover:translate-x-0.5 group-hover:text-[#0454c0]" />
        </span>
      </DestinationLink>
      {/* The count sits outside the link: the link keeps the model's aria-label, and its visible words stay inside it. */}
      {t.count ? (
        <Badge
          variant="outline"
          className="pointer-events-none absolute top-2.5 left-2.5 rounded-full! border-transparent! bg-white/90! px-2! text-[11px]! font-semibold! text-neutral-800! shadow-sm tabular backdrop-blur"
        >
          {t.count}
        </Badge>
      ) : null}
    </div>
  )
}

export function Types({ model }: { model: Model }) {
  const m = model.types
  const n = m.tiles.length
  // On the 3-column grid, the first (and if needed the last) tile runs double width so every row is full.
  const extra = (3 - (n % 3)) % 3
  return (
    <Section aria={m.aria} v2="V05" tone="white">
      <SectionHeader head={m.head} tone="white" />
      <ul className={compactClassName("grid grid-cols-2 gap-3 sm:grid-cols-3 sm:gap-4 lg:grid-cols-4 lg:gap-5 xl:grid-cols-8 xl:gap-3")}>
        {m.tiles.map((t, i) => {
          const wide = (i === 0 && extra >= 1) || (i === n - 1 && extra === 2)
          return (
            <li key={t.href} className={compactClassName(cn(wide && 'sm:col-span-2 lg:col-span-1'))}>
              <TypeTile t={t} i={i} wide={wide} />
            </li>
          )
        })}
      </ul>
      <ChipRows rows={m.chiprows} />
    </Section>
  )
}

/* ------------------------------------------------------------------ V05b: browse by category */

function RailTile({ t, i }: { t: Tile; i: number }) {
  return (
    <DestinationLink href={t.href} aria-label={t.aria} className="group block rounded-2xl text-center focus-visible:outline-offset-[-2px]">
      <span
        className={compactClassName(cn(
          'relative block aspect-square overflow-hidden rounded-2xl bg-gradient-to-b ring-1 ring-black/[0.05] transition duration-300 group-hover:-translate-y-1 group-hover:shadow-[0_18px_32px_-20px_rgba(4,105,240,0.55)] group-hover:ring-[#0469f0]/25',
          well(i + 2),
        ))}
      >
        <Img src={t.img.src} alt={t.img.alt} className="size-full p-3.5 mix-blend-multiply transition duration-500 group-hover:scale-[1.06]" />
      </span>
      <span className={compactClassName("mt-2.5 block text-sm font-semibold leading-snug text-neutral-900 group-hover:text-[#0454c0]")}>{t.label}</span>
    </DestinationLink>
  )
}

/** "Shop by type": the 14 image tiles on the shadcn Carousel. It steps on its own and holds still on hover and focus. */
function TypeRail({ rail }: { rail: Model['categories']['rail'] }) {
  const [api, setApi] = React.useState<CarouselApi>()
  const reduced = useReducedMotion()
  const [paused, setPaused] = React.useState(false)
  const hold = React.useRef(false)
  React.useEffect(() => {
    if (!api) return
    const stop = () => setPaused(true)
    api.on('pointerDown', stop)
    return () => { api.off('pointerDown', stop) }
  }, [api])
  React.useEffect(() => {
    if (!api || reduced || paused) return
    const t = window.setInterval(() => {
      if (hold.current || document.hidden) return
      if (api.canScrollNext()) api.scrollNext()
      else api.scrollTo(0)
    }, 5200)
    return () => window.clearInterval(t)
  }, [api, reduced, paused])
  return (
    <Carousel
      data-auto={5200}
      data-paused={paused || reduced}
      setApi={setApi}
      opts={{ align: 'start', containScroll: 'trimSnaps', slidesToScroll: 'auto', watchDrag: true, duration: reduced ? 0 : 20 }}
      aria-label={`${rail.title} row`}
      className="mt-10 rounded-3xl border border-neutral-200/80 bg-white p-5 shadow-[0_1px_2px_rgba(16,24,40,0.05)] md:p-7"
      onMouseEnter={() => { hold.current = true }}
      onMouseLeave={() => { hold.current = false }}
      onFocusCapture={() => { hold.current = true }}
      onBlurCapture={() => { hold.current = false }}
      onKeyDown={(e) => { if (e.key === 'ArrowLeft' || e.key === 'ArrowRight') setPaused(true) }}
    >
      <div className={compactClassName("mb-5 flex flex-wrap items-end justify-between gap-x-6 gap-y-3")}>
        {/* v2 made this second heading an h2, and v3 keeps that level. */}
        <h2 className={compactClassName("flex items-center gap-2.5 text-xl font-semibold tracking-tight text-neutral-950 md:text-2xl")}>
          {rail.title}
        </h2>
        <div className={compactClassName("flex items-center gap-3")}>
          <ViewAll link={rail.viewAll} variant="link" />
          <RailNav api={api} label={rail.title} onNavigate={() => setPaused(true)} />
        </div>
      </div>
      <CarouselContent className="pt-1.5 pb-1">
        {rail.tiles.map((t, i) => (
          <CarouselItem key={t.href} className="basis-[42%] sm:basis-[27%] md:basis-1/5 lg:basis-[14.2857%]">
            <RailTile t={t} i={i} />
          </CarouselItem>
        ))}
      </CarouselContent>
    </Carousel>
  )
}

export function Categories({ model }: { model: Model }) {
  const m = model.categories
  return (
    <Section id={m.id} aria={m.aria} v2="V05b" tone="mist">
      <SectionHeader head={{ title: m.title, viewAll: m.viewAll }} tone="mist" />
      <ul className={compactClassName("grid grid-cols-2 gap-2.5 md:grid-cols-4 md:gap-3")}>
        {m.links.map((l, i) => {
          const I = iconFor(l.text)
          return (
            <li key={l.href + l.text}>
              <Item
                asChild
                variant="outline"
                size="sm"
                className="h-full flex-nowrap! gap-3! rounded-xl! border-neutral-200! bg-white! px-3! py-2.5! shadow-xs transition! duration-200! hover:-translate-y-0.5 hover:border-[#0469f0]/35! hover:bg-[#f8fbff]! hover:shadow-md"
              >
                <DestinationLink href={l.href} aria-label={l.aria}>
                  <ItemMedia variant="icon" className={cn('size-9! rounded-lg! border-0!', CHIP_TINTS[i % CHIP_TINTS.length])}>
                    <I aria-hidden="true" className="size-[18px]" />
                  </ItemMedia>
                  <ItemContent className="min-w-0">
                    <ItemTitle className="w-auto! text-[13px]! leading-tight! font-semibold! text-neutral-900 sm:text-sm!">{l.text}</ItemTitle>
                  </ItemContent>
                  <ItemActions>
                    <ChevronRight aria-hidden="true" className="size-4 text-neutral-400 transition group-hover/item:translate-x-0.5 group-hover/item:text-[#0454c0]" />
                  </ItemActions>
                </DestinationLink>
              </Item>
            </li>
          )
        })}
      </ul>
      <TypeRail rail={m.rail} />
    </Section>
  )
}

/* ------------------------------------------------------------------ V06: every color */

type ColorCardModel = Model['colors']['cards'][number]

function ColorCard({ c, lit, onSpot }: { c: ColorCardModel; lit: boolean; onSpot: (k: string | null) => void }) {
  const paint = swOf(c.band.style) ?? '#f5f5f5'
  const key = c.band.key
  return (
    <Card
      onMouseEnter={() => key && onSpot(key)}
      onMouseLeave={() => onSpot(null)}
      className={cn(
        'h-full gap-0! overflow-hidden py-0! transition duration-300 hover:-translate-y-1 hover:shadow-lg',
        lit && '-translate-y-1 shadow-lg! ring-2 ring-[#ff9951]/50',
      )}
    >
      {/* The band is a paint chip: the swatch fills it, and the words sit on white tags so they always read. */}
      <h3>
        <DestinationLink
          href={c.band.href}
          className="group/band relative flex h-[72px] items-end justify-between gap-2 border-b border-black/[0.06] px-3 pb-2.5 focus-visible:outline-offset-[-4px]"
          style={{ background: paint }}
        >
          <span aria-hidden="true" className={compactClassName("pointer-events-none absolute inset-0 bg-gradient-to-b from-white/30 via-transparent to-black/[0.04]")} />
          <span className={compactClassName("relative inline-flex min-w-0 items-center rounded-full bg-white/95 px-3 py-1 text-sm font-bold text-neutral-950 shadow-sm ring-1 ring-black/5 transition-colors group-hover/band:text-[#0454c0]")}>
            <span className={compactClassName("truncate")}>{c.band.label}</span>
          </span>
          <span className={compactClassName("relative shrink-0 rounded-full bg-white/95 px-2 py-0.5 text-xs font-bold text-neutral-800 shadow-sm ring-1 ring-black/5 tabular")}>{c.band.count}</span>
        </DestinationLink>
      </h3>
      <ul className={compactClassName("grid grid-cols-3 gap-1.5 p-3")}>
        {c.items.map((it) => (
          <li key={it.href} className={compactClassName("min-w-0")}>
            <DestinationLink href={it.href} className="group/it flex flex-col items-center gap-0.5 rounded-lg p-1 text-center transition-colors hover:bg-neutral-50">
              <span className={compactClassName("mb-1 block aspect-square w-full overflow-hidden rounded-md bg-white")}>
                <Img src={it.img.src} alt={it.img.alt} className="size-full transition duration-300 group-hover/it:scale-[1.07]" />
              </span>
              <span className={compactClassName("tabular text-[13px] font-bold text-neutral-950")}>{it.price}</span>
              <span className={compactClassName("w-full truncate text-[11px] text-neutral-500")}>{it.brand}</span>
            </DestinationLink>
          </li>
        ))}
      </ul>
      <DestinationLink href={c.foot.href} aria-label={c.foot.aria} className="mt-auto inline-flex items-start gap-1.5 px-4 pt-1 pb-4 text-[13px] font-semibold text-[#0454c0] hover:underline">
        {words(c.foot.text)}
        <ArrowRight aria-hidden="true" className="mt-0.5 size-3.5 shrink-0" />
      </DestinationLink>
    </Card>
  )
}

function RetroPromo({ p }: { p: Model['colors']['promo'] }) {
  return (
    <div className={compactClassName("relative mt-10 overflow-hidden rounded-3xl border border-[#ffe1cc] bg-gradient-to-br from-[#fff7f0] via-[#fff8ec] to-[#eaf4ff] px-6 py-10 md:px-12 md:py-12")}>
      <div aria-hidden="true" className={compactClassName("pointer-events-none absolute inset-0")}>
        <div className={compactClassName("absolute -top-24 right-[-60px] size-[420px] rounded-full bg-[conic-gradient(from_210deg,#ff8fa3,#ffd166,#9ae6b4,#90cdf4,#c3a6ff,#ff8fa3)] opacity-35 blur-3xl")} />
        <div className={compactClassName("absolute -bottom-28 -left-20 size-[300px] rounded-full bg-[radial-gradient(circle,rgba(255,153,81,0.25),transparent_65%)]")} />
      </div>
      <div className={compactClassName("relative grid items-center gap-8 md:grid-cols-[1.15fr_0.85fr] md:gap-10")}>
        <div>
          <DestinationLink
            href={p.kicker.href}
            aria-label={p.kicker.aria}
            className="inline-flex items-center gap-1.5 rounded-full bg-white/85 px-3 py-1 text-xs font-bold uppercase tracking-[0.12em] text-[#be123c] ring-1 ring-[#be123c]/15 transition hover:bg-white hover:ring-[#be123c]/30"
          >
            <Award aria-hidden="true" className="size-3.5" />
            {words(p.kicker.text)}
          </DestinationLink>
          <h2 className={compactClassName("mt-4 text-balance text-4xl font-bold leading-[1.05] tracking-tight text-neutral-950 md:text-5xl")}>{p.title}</h2>
          <p className={compactClassName("mt-4 max-w-xl text-pretty text-base leading-relaxed text-neutral-700 md:text-lg")}>{p.text}</p>
          <div className={compactClassName("mt-7 flex flex-wrap gap-3")}>
            {p.ctas.map((c, k) => (
              <Button
                key={c.href}
                asChild
                size="lg"
                variant={k === 0 ? 'ctaPrimary' : 'outline'}
                className={cn('h-12! rounded-full! px-6! text-[15px]! font-semibold!', k > 0 && 'bg-white! hover:border-[#0469f0]! hover:bg-[#f3f8ff]! hover:text-[#0454c0]!')}
              >
                <DestinationLink href={c.href} aria-label={c.aria}>
                  {words(c.text)}
                  {k === 0 ? <ArrowRight aria-hidden="true" /> : null}
                </DestinationLink>
              </Button>
            ))}
          </div>
        </div>
        <div className={compactClassName("relative mx-auto flex w-full max-w-[19rem] items-end justify-center md:max-w-sm")}>
          <span aria-hidden="true" className={compactClassName("absolute inset-x-[16%] bottom-1 h-6 rounded-[50%] bg-[#d63031]/20 blur-xl")} />
          <span data-motion className={compactClassName("relative block w-full")} style={{ animation: 'v3-float 6.5s ease-in-out infinite' }}>
            <Img src={p.img.src} alt={p.img.alt} className="h-72 w-full md:h-[22rem]" />
          </span>
        </div>
      </div>
    </div>
  )
}

/**
 * The swatch spotlight from v2: hovering or focusing a swatch lights its colour card, and while the row is on screen
 * the light steps through the swatches by itself. It holds while the pointer or keyboard focus is in the section,
 * and reduced motion turns the stepping off.
 */
function useSpotlight(keys: string[], ref: React.RefObject<HTMLElement | null>) {
  const reduced = useReducedMotion()
  const [auto, setAuto] = React.useState<string | null>(null)
  const [user, setUser] = React.useState<string | null>(null)
  const hold = React.useRef(false)
  React.useEffect(() => {
    const el = ref.current
    if (!el || reduced || !keys.length || typeof IntersectionObserver === 'undefined') return
    let seen = false
    const io = new IntersectionObserver(([e]) => { seen = e.isIntersecting }, { threshold: 0.25 })
    io.observe(el)
    let i = -1
    const t = window.setInterval(() => {
      if (!seen || hold.current || document.hidden) return
      i = (i + 1) % keys.length
      setAuto(keys[i])
    }, 2600)
    return () => { io.disconnect(); window.clearInterval(t) }
  }, [reduced, keys, ref])
  const holdOn = () => { hold.current = true }
  const holdOff = () => { hold.current = false }
  return { active: user ?? (reduced ? null : auto), setUser, holdOn, holdOff }
}

export function Colors({ model }: { model: Model }) {
  const m = model.colors
  const rowRef = React.useRef<HTMLUListElement>(null)
  const cardsRef = React.useRef<HTMLUListElement>(null)
  const swatchDrag = useDragScroll(rowRef)
  const cardDrag = useDragScroll(cardsRef)
  const keys = React.useMemo(() => m.swatches.map((s) => s.key), [m.swatches])
  const spot = useSpotlight(keys, rowRef)
  return (
    <Section aria={m.aria} v2="V06" tone="white" className="overflow-hidden">
      <div role="presentation" onMouseEnter={spot.holdOn} onMouseLeave={spot.holdOff} onFocusCapture={spot.holdOn} onBlurCapture={spot.holdOff}>
        <SectionHeader head={m.head} tone="white" />
        <ul
          ref={rowRef}
          {...swatchDrag}
          data-auto={2600}
          className={compactClassName("v3-noscrollbar -mx-4 flex snap-x scroll-px-4 gap-2 overflow-x-auto px-4 py-1.5 sm:mx-0 sm:flex-wrap sm:overflow-visible sm:px-0")}
        >
          {m.swatches.map((s) => {
            const on = spot.active === s.key
            return (
              <li key={s.key} className={compactClassName("shrink-0 snap-start")}>
                <DestinationLink
                  href={s.href}
                  onMouseEnter={() => spot.setUser(s.key)}
                  onMouseLeave={() => spot.setUser(null)}
                  onFocus={() => spot.setUser(s.key)}
                  onBlur={() => spot.setUser(null)}
                  className={cn(
                    'group inline-flex h-11 items-center gap-2.5 whitespace-nowrap rounded-full border bg-white pr-4 pl-1.5 text-sm font-medium text-neutral-800 shadow-xs transition duration-200 hover:-translate-y-0.5 hover:border-neutral-300 hover:shadow-md',
                    on ? 'border-[#ff9951] ring-2 ring-[#ff9951]/30' : 'border-neutral-200',
                  )}
                >
                  <span
                    aria-hidden="true"
                    className={compactClassName(cn('size-8 rounded-full shadow-[inset_0_0_0_1px_rgba(16,24,40,0.16),0_4px_10px_-6px_rgba(16,24,40,0.5)] transition duration-200 group-hover:scale-110', on && 'scale-110'))}
                    style={{ background: s.sw }}
                  />
                  {s.label}
                  <span className={compactClassName("tabular text-xs font-semibold text-neutral-500")}>{' '}{s.count}</span>
                </DestinationLink>
              </li>
            )
          })}
        </ul>
        <ul ref={cardsRef} {...cardDrag} className={compactClassName("v3-noscrollbar -mx-4 mt-8 flex snap-x snap-mandatory scroll-px-4 gap-4 overflow-x-auto px-4 py-2 sm:mx-0 sm:grid sm:grid-cols-2 sm:overflow-visible sm:px-0 sm:py-0 lg:grid-cols-4 lg:gap-5")}>
          {m.cards.map((c) => (
            <li key={c.band.href} className={compactClassName("w-[82%] shrink-0 snap-start sm:w-auto")}>
              <ColorCard c={c} lit={!!c.band.key && spot.active === c.band.key} onSpot={spot.setUser} />
            </li>
          ))}
        </ul>
        <RetroPromo p={m.promo} />
      </div>
    </Section>
  )
}

/* ------------------------------------------------------------------ V07: mini fridges */

/** Photo-well tints for the lineup, keyed by the word each name starts with ("Red · …"). */
const MINI_WELL: Record<string, string> = {
  red: 'from-[#ffdfe2] to-[#fff5f6]',
  pink: 'from-[#ffe0ef] to-[#fff6fb]',
  blue: 'from-[#dae7ff] to-[#f4f8ff]',
  green: 'from-[#d9f2e1] to-[#f4fbf6]',
  black: 'from-[#e2e5ea] to-[#f7f8fa]',
  white: 'from-[#edf0f4] to-[#fcfdfe]',
  gray: 'from-[#e6e9ed] to-[#f8f9fa]',
  silver: 'from-[#e8ebef] to-[#fafbfc]',
  stainless: 'from-[#e8ebef] to-[#fafbfc]',
  cream: 'from-[#f8eed5] to-[#fffbf2]',
  retro: 'from-[#ffe2ee] via-[#fff3da] to-[#e0efff]',
  dorm: 'from-[#ffefc9] to-[#fffaf0]',
  portable: 'from-[#d4f3ee] to-[#f2fcfa]',
}
/** Dot colours for words that are not a colour swatch in the model. */
const MINI_DOT: Record<string, string> = { pink: '#f472b6', dorm: '#f5a524', portable: '#0469f0' }

const splitName = (name: string) => {
  const i = name.indexOf(' · ')
  return i > 0 ? { tag: name.slice(0, i), rest: name.slice(i + 3) } : { tag: '', rest: name }
}

export function Mini({ model }: { model: Model }) {
  const m = model.mini
  const paint = (word: string) => model.colors.swatches.find((s) => s.label.toLowerCase() === word)?.sw ?? MINI_DOT[word] ?? '#d4d4d4'
  return (
    <Section aria={m.aria} v2="V07" tone="peach" className="overflow-hidden">
      <div aria-hidden="true" className={compactClassName("pointer-events-none absolute inset-0")}>
        <div className={compactClassName("absolute -top-32 -left-24 size-[460px] rounded-full bg-[radial-gradient(circle,rgba(244,114,182,0.16),transparent_65%)]")} />
        <div className={compactClassName("absolute top-10 right-[-140px] size-[520px] rounded-full bg-[radial-gradient(circle,rgba(4,105,240,0.12),transparent_65%)]")} />
        <div className={compactClassName("absolute -bottom-40 left-1/3 size-[480px] rounded-full bg-[radial-gradient(circle,rgba(16,166,92,0.12),transparent_65%)]")} />
      </div>
      <div className={compactClassName("relative grid items-center gap-8 lg:grid-cols-[minmax(0,0.9fr)_minmax(0,1.1fr)] lg:gap-10")}>
        <div>
          <Eyebrow tone="peach">{m.eyebrow}</Eyebrow>
          <h2 className={compactClassName("mt-3 text-balance text-4xl font-bold leading-[1.05] tracking-tight text-neutral-950 md:text-5xl lg:text-[3.35rem]")}>
            {m.title[0]}{' '}
            <span className={compactClassName("relative inline-block bg-[linear-gradient(90deg,#e11d48,#c026d3_32%,#0469f0_66%,#059669)] bg-clip-text pb-1 text-transparent")}>
              {m.title[1]}
              <svg aria-hidden="true" viewBox="0 0 300 16" preserveAspectRatio="none" className={compactClassName("absolute -bottom-1.5 left-0 h-2.5 w-full md:h-3")}>
                <path d="M3 11C60 4 110 4 160 8s100 5 137-4" fill="none" stroke="#ff9951" strokeWidth="5" strokeLinecap="round" />
              </svg>
            </span>
          </h2>
          <p className={compactClassName("mt-5 max-w-xl text-pretty text-base leading-relaxed text-neutral-700 md:text-lg")}>{m.sub}</p>
          <ChipLinks links={m.chips} className="mt-6" />
          <div className={compactClassName("mt-7 flex flex-wrap gap-3")}>
            {m.ctas.map((c, k) => (
              <Button
                key={c.href}
                asChild
                size="lg"
                variant={k === 0 ? 'ctaPrimary' : 'outline'}
                className={cn('h-12! rounded-full! px-6! text-[15px]! font-semibold!', k > 0 && 'bg-white! hover:border-[#0469f0]! hover:bg-[#f3f8ff]! hover:text-[#0454c0]!')}
              >
                <DestinationLink href={c.href} aria-label={c.aria}>
                  {words(c.text)}
                  {k === 0 ? <ArrowRight aria-hidden="true" /> : null}
                </DestinationLink>
              </Button>
            ))}
          </div>
        </div>
        <ul className={compactClassName("grid grid-cols-2 gap-x-3 gap-y-5 sm:grid-cols-4 sm:gap-x-4")}>
          {m.lineup.map((f, i) => {
            const { tag, rest } = splitName(f.name)
            const word = tag.toLowerCase()
            return (
              <li key={f.href}>
                <DestinationLink href={f.href} className="group relative flex h-full flex-col items-center gap-1.5 text-center">
                  <span
                    className={compactClassName(cn(
                      'relative mb-1 grid aspect-[1/1.08] w-full place-items-end overflow-hidden rounded-3xl bg-gradient-to-b px-3 pt-9 ring-1 ring-black/[0.05] transition duration-300 group-hover:-translate-y-1 group-hover:-rotate-1 group-hover:shadow-[0_18px_30px_-18px_rgba(16,24,40,0.45)]',
                      MINI_WELL[word] ?? well(i),
                    ))}
                  >
                    <span aria-hidden="true" className={compactClassName("absolute inset-x-[20%] bottom-2 h-3 rounded-[50%] bg-[#1a1a1a]/10 blur-md")} />
                    {/* The float runs on the photo itself: a moving wrapper would isolate it, and the multiply blend that
                        drops the photo's white backdrop into the tint needs the well behind it. */}
                    <Img
                      src={f.img.src}
                      alt={f.img.alt}
                      data-motion
                      className="relative h-[92%] w-full mix-blend-multiply"
                      style={{ animation: `v3-float 5.5s ease-in-out ${(-i * 0.6).toFixed(1)}s infinite` }}
                    />
                  </span>
                  <Badge
                    variant="outline"
                    className="absolute top-2.5 right-2.5 rounded-full! border-transparent! bg-white! px-2.5! py-1! text-[13px]! font-bold! text-neutral-950! shadow-[0_6px_14px_-8px_rgba(16,24,40,0.5)] tabular"
                  >
                    {f.price}{' '}
                  </Badge>
                  <span className={compactClassName("line-clamp-2 px-1 text-[13px] font-medium leading-snug text-neutral-700")}>
                    {tag ? (
                      <span className={compactClassName("mr-1 inline-flex items-center gap-1 font-semibold text-neutral-950")}>
                        <span aria-hidden="true" className={compactClassName("inline-block size-2.5 rounded-full ring-1 ring-black/15")} style={{ background: paint(word) }} />
                        {tag}
                      </span>
                    ) : null}
                    {rest}
                  </span>
                  <span className={compactClassName("inline-flex items-center gap-1.5 text-xs text-neutral-600")}><RetailerMark name={f.at.replace(/^Lowest at /, '')} />{f.at}</span>
                </DestinationLink>
              </li>
            )
          })}
        </ul>
      </div>
    </Section>
  )
}
